<template>
  <div class="sh-login-page-container">
    <aside class="sh-login-left-panel">
      <div class="sh-left-panel-content">
        <div class="sh-logo-section">
          <img class="sh-logo-image" src="/rallyhood-white.svg" alt="Rallyhood" />
        </div>

        <div class="sh-welcome-block sh-left-panel-desktop-only">
          <h1 class="sh-welcome-heading-top">Welcome back!</h1>
          <p class="sh-welcome-subheading">Your Rallies missed you.</p>
          <p class="sh-login-prompt">
            Log in to pick up right where you left off &mdash; events, messages, and all the good stuff are waiting.
          </p>
        </div>
      </div>
    </aside>

    <!-- Premium card lives outside both panels so the same markup can live
         in two visual contexts: absolutely positioned over the bottom of
         the teal left panel on desktop, naturally flowing in the column
         layout between the panels on tablet / mobile. -->
    <div class="sh-premium-card-wrap">
      <span class="sh-dot sh-dot--bl"></span>
      <i class="fa-solid fa-sparkle sh-sparkle sh-sparkle--teal sh-sparkle--tr" aria-hidden="true"></i>
      <i class="fa-solid fa-sparkle sh-sparkle sh-sparkle--light sh-sparkle--mr" aria-hidden="true"></i>
      <i class="fa-solid fa-sparkle sh-sparkle sh-sparkle--light sh-sparkle--bl" aria-hidden="true"></i>
      <i class="fa-solid fa-sparkle sh-sparkle sh-sparkle--light sh-sparkle--br" aria-hidden="true"></i>

      <div class="sh-premium-card">
        <h3 class="sh-premium-card-title">
          Want to make organizing your<br />groups even easier?
        </h3>
        <p class="sh-premium-card-body">
          Upgrade to <a class="sh-premium-link" href="https://www.rallyhood.com/home/request-a-demo.php" target="_blank" rel="noopener">Premium</a>
          for advanced tools to manage sub-groups, schedule content, track engagement and leverage other analytics.
        </p>
      </div>
    </div>

    <section class="sh-login-right-panel">
      <div class="sh-right-panel-content">
        <div>
          <h2 class="sh-login-form-heading">Log Into Your Account</h2>

          <form class="sh-login-form" autocomplete="on" @submit.prevent="handleSubmit" ref="loginform">
            <div class="sh-form-group">
              <label for="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="text"
                autocomplete="username"
                class="sh-form-input"
                :class="{ 'sh-form-input--error': emailError }"
                @input="onEmailInput"
                @click="scrollTop"
              />
              <p v-if="emailError" class="sh-form-field-error">{{ emailError }}</p>
            </div>

            <div class="sh-form-group sh-form-group--password">
              <label for="password">Password</label>
              <div class="sh-password-input-container">
                <input
                  id="password"
                  name="password"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  class="sh-form-input sh-password-input"
                  :class="{ 'sh-form-input--error': passwordError }"
                  @input="onPasswordInput"
                />
                <button
                  type="button"
                  class="sh-toggle-password"
                  @click="togglePassword"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                >
                  <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <div class="sh-forgot-password">
              <router-link class="sh-forgot-link" to="/resetpasswordemail">Forgot your password?</router-link>
              <p v-if="passwordError" class="sh-form-field-error sh-form-field-error--below-forgot">{{ passwordError }}</p>
            </div>

            <button type="submit" class="sh-login-button" :disabled="status.loggingIn">
              Log In
            </button>
          </form>

          <div class="sh-login-divider"></div>

          <div class="sh-create-account-section">
            <p class="sh-no-account-text">Don't have an account?</p>
            <router-link class="sh-create-account-button" to="/createAccount">Create Account</router-link>
          </div>
        </div>

        <div class="sh-right-footer">
          <a href="mailto:support@rallyhood.com">Contact Us</a>
          <span class="sh-footer-sep" aria-hidden="true">&bull;</span>
          <a href="https://www.rallyhood.com/home/terms.php" target="_blank" rel="noopener">Terms &amp; Conditions</a>
          <span class="sh-footer-sep" aria-hidden="true">&bull;</span>
          <a href="https://www.rallyhood.com/home/privacy" target="_blank" rel="noopener">Privacy</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { userInfoPersistent } from '@/common/user-info';
import { RH_URL } from "../common";
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { Browser } from '@capacitor/browser'; 
import { useGate } from '@/composables/useGate';
import '@/stylesheets/sh-login.css';

export default {
  name: 'LoginPage',
  setup() {
    const { email, timezone, claimedUser, invitationToken } = useGate();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const loginform = ref(null);
    const username = ref('');
    const password = ref('');
    const showPassword = ref(false);
    const screenWidth = ref(window.innerWidth);
    const emailError = ref('');
    const passwordError = ref('');

    const status = computed(() => store.state.user.status);
    const user = computed(() => store.state.user.user);

    // Simple email format check, used only to flag obvious typos client-side.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const togglePassword = () => {
      showPassword.value = !showPassword.value;
    };

    const handleResize = () => {
      screenWidth.value = window.innerWidth;
    };

    const onEmailInput = (event) => {
      username.value = event.target.value;
      if (emailError.value) emailError.value = '';
    };

    const onPasswordInput = (event) => {
      password.value = event.target.value;
      if (passwordError.value) passwordError.value = '';
    };

    // Server login failure is surfaced as status.error. We treat that as a
    // credentials problem and pin the message under the password field.
    watch(status, (newStatus) => {
      if (newStatus && newStatus.error) {
        passwordError.value = 'Incorrect password';
      }
    });

    const handleSubmit = async () => {
      emailError.value = '';
      passwordError.value = '';

      const trimmed = username.value.trim();
      if (!trimmed || !EMAIL_RE.test(trimmed)) {
        emailError.value = 'Invalid email address';
        return;
      }

      if (username.value && password.value) {
        await store.dispatch('user/login', { username: trimmed, password: password.value.trim() });
      }
      scrollBack();
    };

    const scrollBack = () => {
      const login_form = loginform.value;
      if (login_form) {
        login_form.style = '';
      }
    };

    const scrollTop = () => {
      if (screenWidth.value < 960) {
        const login_form = loginform.value;
        if (login_form) {
          login_form.style = 'position: fixed; top: 25px; width: 100%';
        }
      }
    };

const setupListener = async () => {
    try {
      const user = await userInfoPersistent();
      if (Capacitor.getPlatform() != "web") {
        await store.dispatch('user/loadPushStuff', { user });
      }
    } catch (e) {
      console.log('error loading push stuff in login page', e);
    }
}
   

    const logout = async () => {
      localStorage.removeItem('at');
      localStorage.removeItem('user');
      await Preferences.remove({ key: 'user' });
    };

    onMounted(async () => {
      window.addEventListener('resize', handleResize);
      const user = await userInfoPersistent();
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        await logout();
      }
    });

    onUnmounted(() => {
      setupListener();
      window.removeEventListener('resize', handleResize);
    });

    watch(user, (newval) => {
      if (invitationToken.value) {
        const rallyId = route.query.rallyId;
        router.push({
          path: `/${rallyId}/igate`,
          query: { ...route.query }
        });
      } else if (route.query.redirect) {
        router.push({ path: route.query.redirect });
      } else {
        router.push('/rallies');
      }
    });


    return {
      username,
      password,
      showPassword,
      screenWidth,
      status,
      emailError,
      passwordError,
      togglePassword,
      handleSubmit,
      onEmailInput,
      onPasswordInput,
      scrollBack,
      scrollTop,
      email,
      timezone,
      claimedUser,
      invitationToken,
    };
  }
};
</script>

